import { defaultTo, flow, get, hasIn, isNil } from 'lodash';
import { connect } from 'react-redux';
import { AppThunkDispatch, AppState } from 'src/store';
import { ReceiptGridOwnProps, ReceiptGridValueProps, ReceiptGridDispatchProps } from './ReceiptGrid.types';

import { makePrintSensitive } from 'src/components/higherOrder/Print/PrintSenstive';
import { makePopoverSensitive } from '../AssortmentStyleDetailsPopover/AssortmentStyleDetailsPopover';
import {
  updateConfigurableGridConfig,
  refreshConfigurableGridData,
  requestTopAttributesData,
} from 'src/components/ConfigurableGrid/ConfigurableGrid.slice';
import { bindActionCreators } from 'redux';
import { FabType, getfabProps, withFab } from '../higherOrder/withFab';
import { updateAssortmentPlan } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.actions';
import { isViewDefnLoaded } from 'src/dao/tenantConfigClient';
import { isDataLoaded } from 'src/services/pivotServiceCache';
import { ConfigurableGrid } from './ReceiptGrid';
import {
  getViewDefnData,
  getConfigurableGridData,
  ConfigurableGridDataSelectorProps,
  getGroupedConfigurableGridData,
  getGroupByDropdownProps,
  getGroupBySelection,
  getNeededFilterInfo,
} from 'src/components/ConfigurableGrid/ConfigurableGrid.selectors';
import { ConfigurableGridColumnDef } from 'src/components/ConfigurableGrid/ConfigurableGrid.types';
import { submitPayload } from 'src/pages/AssortmentBuild/FlowSheet/FlowSheetByStyle.slice';
import { SubmitPayload, BasicPivotItem } from 'src/worker/pivotWorker.types';
import { AdornmentType } from 'configuration-schemas/viewdefns/literals';
import { AgFlatResult } from 'src/utils/Component/AgGrid/AgDataFormat';
import {
  updateTopMemberSelection,
  updateGroupBy,
  updateCompanionSortField,
  updateCompanionSortDirection,
} from '../Subheader/Subheader.slice';
import { SortByDirection } from '../Subheader/Subheader.types';
import { toggleCompanionCollapse } from 'src/state/uipose/UiPose.slice';
import { externalGridSearchFields } from 'src/utils/Domain/Constants';
import { filterData } from 'src/utils/Pivot/Filter';

interface ReceiptGridProps extends ReceiptGridValueProps, ReceiptGridOwnProps {}
function mapStateToProps(state: AppState, ownProps: ReceiptGridOwnProps): ReceiptGridProps {
  const showFlow = defaultTo(ownProps.showFlowStatus, true);
  const {
    viewDefnState,
    gridDataState: viewDataState,
    viewDefn,
    unmodifiedViewDefn,
  } = state.pages.assortmentBuild.configurableGrid;
  const configLoaded = isViewDefnLoaded(viewDefnState);
  const dataLoaded = isDataLoaded(viewDataState);

  const columnDefs = isNil(viewDefn) ? [] : (viewDefn.columns as ConfigurableGridColumnDef[]);
  const detailColumnDefs = isNil(viewDefn) ? [] : (viewDefn.detailColumns as ConfigurableGridColumnDef[]);
  const gridRowHeight: number = get(viewDefn, 'main.rowHeight', 30);
  const detailRowCountKey: string | null = get(viewDefn, 'main.detailRowCountKey', null);
  const massEditConfig = hasIn(viewDefn, 'massEditConfig') ? viewDefn.massEditConfig : undefined;
  const fetchDetails = hasIn(viewDefn, 'actions.fetchDetails') ? get(viewDefn, 'actions.fetchDetails') : undefined;
  const dataRules = hasIn(viewDefn, 'dataRules') ? viewDefn.dataRules : [];
  const showPublish = hasIn(viewDefn, 'showPublish') ? viewDefn.showPublish : undefined;
  const updateCoordinateMap = hasIn(viewDefn, 'updateCoordinateMap') ? viewDefn.updateCoordinateMap : undefined;
  const salesAdjustmentConfig = isNil(viewDefn) ? {} : viewDefn.salesAdjustment;
  const adornments: AdornmentType[] = get(viewDefn, 'adornments', []);

  const { dependentCalcs, companionSortOptions, defaultCompanionSortField, clientActionHandlers } =
    getViewDefnData(state);
  const groupByDropdownProps = getGroupByDropdownProps(state);
  const floorsetDropdownProps = undefined;
  const selectorProps: ConfigurableGridDataSelectorProps = {
    showFlowStatus: showFlow,
    leafId: '',
  };
  const groupedData: AgFlatResult | undefined = getGroupedConfigurableGridData(state, selectorProps);
  const ungroupedData: BasicPivotItem[] = getConfigurableGridData(state);
  const flatData: BasicPivotItem[] =
    isNil(groupedData) || isNil(groupByDropdownProps?.selection) ? ungroupedData : groupedData.agFlatTree;
  const treeColumnDefinition = isNil(groupedData) ? undefined : groupedData.treeColumnDefinition;
  const { fabType = FabType.none } = ownProps;
  const fabProps = getfabProps(state, fabType);

  return {
    ...ownProps,
    fetchDetails,
    flowStatus: showFlow ? state.subheader.flowStatus : [],
    showFlowStatus: showFlow,
    search: state.subheader.search || '',
    groupBySelection: getGroupBySelection(state),
    favoritesList: state.subheader.favoritesList || [],
    fab: fabProps,
    configLoaded,
    dataLoaded,
    columnDefs,
    detailColumnDefs,
    dataRules,
    massEditConfig,
    showPublish,
    updateCoordinateMap,
    dependentCalcs,
    gridRowHeight,
    detailRowCountKey,
    companionSortOptions,
    defaultCompanionSortField,
    unmodifiedViewDefn,
    configuratorViewDefn: viewDefn,
    clientActionHandlers,
    groupByDropdownProps,
    floorsetDropdownProps,
    data: flatData,
    topAttributesData: state.pages.assortmentBuild.configurableGrid.topAttributesData,
    salesAdjustmentConfig,
    adornments,
    treeColumnDefinition,
    viewDataState,
    sideNavCollapsed: state.sidenav.collapsed,
    isCompanionCollapsed: state.uiPose.isCompanionCollapsed,
    companionSortField: state.subheader.companionSortField,
    companionSortDirection: state.subheader.companionSortDirection,
  };
}

function mapDispatchToProps(dispatch: AppThunkDispatch, ownProps: ReceiptGridOwnProps): ReceiptGridDispatchProps {
  return {
    dispatch,
    ...bindActionCreators(
      { updateGroupBy, updateTopMemberSelection, onToggleCompanion: toggleCompanionCollapse },
      dispatch,
    ),
    onUpdateConfig: (config: any) => {
      dispatch(updateConfigurableGridConfig(config));
    },
    updateAssortmentPlan: () => {
      dispatch(updateAssortmentPlan('PlanQueue'));
    },
    onRefreshConfigurableGridData: () => {
      dispatch(refreshConfigurableGridData());
    },
    onRefreshTopAttributes: () => {
      dispatch(requestTopAttributesData());
    },
    submitPayload: async (payload: SubmitPayload) => {
      await submitPayload(payload)();
    },
    addSelectedItemsToCart() {
      // do nothing for now
    },
    toggleSelections(_items: BasicPivotItem[]) {
      // implement when worklist selection is enabled in this component
    },
    syncGridData(_redecoratedData) {
      // do nothing for now
    },
    doesRowPassFilter(row: BasicPivotItem) {
      return dispatch((_dispatch, getState) => {
        const state = getState();
        const { flowStatus, search, viewDefn } = getNeededFilterInfo(state);
        const finalFlowStatus = ownProps.showFlowStatus ? flowStatus : [];
        const configSearchOrDefafult = get(viewDefn, 'searchIndexes', externalGridSearchFields) as string[];
        const rows = filterData([row], search, configSearchOrDefafult, finalFlowStatus);
        return rows.length > 0;
      });
    },
    updateCompanionSortField: (field: string) => {
      dispatch(updateCompanionSortField(field));
    },
    updateCompanionSortDirection: (dir: SortByDirection) => {
      dispatch(updateCompanionSortDirection(dir));
    },
  };
}

const sensitiveView = flow(() => ConfigurableGrid, withFab, makePrintSensitive, makePopoverSensitive)();
export default connect(mapStateToProps, mapDispatchToProps)(sensitiveView);
